import { useEffect, useMemo, useState } from 'react';
import Image from "next/image";
import { Form } from "react-bootstrap";
import { useTranslations } from 'next-intl';

import styles from "@/styles/item.module.scss";
import { formatCurrency, modifiersFilter, removeSpacesFromString } from '_services/common.service';
import { Minus, Plus, X } from 'react-feather';
import { Modifier, ModifierOption } from '../pages/itemview';
import CustomTooltip from '../common/customtooltip';
import RemoveCustomizeConfirmation from './remove-customize-confirmation';
import { alertService } from '_services/alert.service';
import { event } from '@lib/ga';

export default function CustomizeSelector({ 
    isHalfAndHalf,
    isBottomSheet = false,
    isShowDietaryOptions = false,
    showCustomize,
    ItemNameForAlert, 
    currentStepCount,
    fullStepCount,
    showingTabId,
    modifiers = [],
    onAddToCart,
    setModifiers,
    totalPrice,
    setShowCustomize,
    setPauseNextStep,
    handleModifierChange,
    resetModifiers,
}: {
    isHalfAndHalf: boolean;
    isBottomSheet: boolean;
    isShowDietaryOptions: boolean;
    showCustomize: boolean;
    ItemNameForAlert?:string ;
    stepCount?: string;
    currentStepCount?: number;
    fullStepCount?: number;
    showingTabId:string;
    modifiers?: Modifier[];
    onAddToCart?: () => void;
    setModifiers: (modifiers: Modifier[]) => void;
    totalPrice?: string;
    setShowCustomize: (show: boolean) => void;
    setPauseNextStep: (show: boolean) => void;
    handleModifierChange: (optionId: string, index: number, isQuantityChange: boolean, quantityChange: unknown, isHalfAndHalf: boolean) => void;
    resetModifiers?: () => void;
    saveOrder?: () => void;
}) {
  const t = useTranslations();
  const [activeTab, setActiveTab] = useState(0);
  // console.log(modifiers);

  // useEffect(() => {
  //   // Reset active tab when modal is opened
  //   if (showCustomize) {
  //     setActiveTab(0);
  //     const existModifiers = structuredClone(modifiers);
  //     console.log(existModifiers);
  //   }
  // }, [showCustomize]);

  const [existModifiers, setExistModifiers] = useState<Modifier[] | null>(null);
  const [showRemoveCustomizeConfirmation, setShowRemoveCustomizeConfirmation] = useState(false);
  const [showValidationMsg, setShowValidationMsg] = useState(false);
  const [isReset, setIsReset] = useState(false);
  const [localModifiers, setLocalModifiers] = useState<Modifier[]>(() =>
    structuredClone(modifiers)
  );

  // Sync local copy when the sheet opens
  useEffect(() => {
    if (showCustomize) {
      // setActiveTab(0);
      const fresh = structuredClone(modifiers);
      setLocalModifiers(fresh);
      setExistModifiers(structuredClone(modifiers));
      setIsReset(false);

      setShowValidationMsg(false);

      // Filter modifiers the same way as in the render
      const filteredModifiers = modifiersFilter(
        modifiers || [], 
        isShowDietaryOptions ? ['dietary', 'notdietary'] : ['notdietary']
      );

      // Find the index in the FILTERED array
      if (showingTabId) {
        const matchingIndex = filteredModifiers.findIndex(
          (modifier) => modifier.modifierId === showingTabId
        );

        setActiveTab(matchingIndex !== -1 ? matchingIndex : 0);
        setShowValidationMsg(true)
      } else {
        setActiveTab(0);
      }
    }
  }, [showCustomize]);

  useEffect(() => {
    if (showCustomize) {
      setLocalModifiers(structuredClone(modifiers));
    }
  }, [JSON.stringify(modifiers)]);

  const invalidModifiers = useMemo(() => {
    if (!localModifiers?.length) return [];

    return localModifiers.reduce<{ modifierId: string; modifierName: string ;minCount:number }[]>(
      (acc, mod) => {
        if (mod.minSelection > 0) {
          const appliedCount =
            mod.mofireOptions?.filter((opt) => opt.isApplied === true).length ?? 0;
          if (appliedCount < mod.minSelection) {
            acc.push({ modifierId: mod.modifierId, modifierName: mod.modifierName ,minCount:mod.minSelection});
          }
        }
        return acc;
      },
      []
    );
  }, [localModifiers]); 

  useEffect(() => {
    setPauseNextStep(showRemoveCustomizeConfirmation);
  }, [showRemoveCustomizeConfirmation]);

  const handleOptionChange = (
    modifierId: string,
    optionIndex: number,
    isQuantityChange: boolean,
    quantityChange: unknown,
    halfAndHalf: boolean
  ) => {
    // 1. Notify parent
    handleModifierChange(modifierId, optionIndex, isQuantityChange, quantityChange, halfAndHalf);

    // 2. Mirror change in local copy so invalidModifiers recomputes immediately
    setLocalModifiers((prev) => {
      const updated = structuredClone(prev);
      const mod = updated.find((m) => m.modifierId === modifierId);
      if (!mod || !mod.mofireOptions) return prev;

      const opt = mod.mofireOptions[optionIndex];
      if (!opt) return prev;

      if (isQuantityChange) {
        const delta = quantityChange as number;
        const current = Number(opt.quantity) || 0;
        const next = current + delta;
        if (next <= 0) {
          opt.isApplied = false;
          opt.quantity = 0;
        } else {
          opt.isApplied = true;
          opt.quantity = next;
        }
      } else {
        const newApplied = quantityChange as boolean;

        if (mod.selectionType === 'SINGLE') {
          // Radio — deselect all others first
          mod.mofireOptions.forEach((o) => {
            o.isApplied = false;
            o.quantity = 0;
          });
        }

        if (mod.selectionType === 'MULTIPLE' && newApplied) {
          const currentAppliedCount = mod.mofireOptions.filter((o) => o.isApplied).length;
          if (mod.maxSelection > 0 && currentAppliedCount >= mod.maxSelection) {
            return prev; 
          }
        }

        opt.isApplied = newApplied;
        if (newApplied && (Number(opt.quantity) || 0) === 0) {
          opt.quantity = 1;
        }
        if (!newApplied) {
          opt.quantity = 0;
        }
      }

      return updated; 
    });
  };

  const handleReset = () => {
    resetModifiers?.();
    setIsReset(true);
  };

  const handleModelClose = () => {
    setShowCustomize(false);

    const existFiltered = modifiersFilter(
      existModifiers || [],
      isShowDietaryOptions ? ['dietary', 'notdietary'] : ['notdietary']
    );
    const currentFiltered = modifiersFilter(
      localModifiers || [],
      isShowDietaryOptions ? ['dietary', 'notdietary'] : ['notdietary']
    );

    if (JSON.stringify(existFiltered) !== JSON.stringify(currentFiltered) && !isReset) {
      setShowRemoveCustomizeConfirmation(true);
      setPauseNextStep(true);
    }
  };

  const isFinishStep =
    !currentStepCount || !fullStepCount || currentStepCount >= fullStepCount;

  return (<>
    <div className={showCustomize ? `${styles.bottomSheetBackdrop} ${styles.showBottomSheetBackdrop}` : styles.bottomSheetBackdrop}/>

      {showCustomize? <div className={styles.customBackDrop} /> : <></>}

      <div className={`${styles.bottomSheet} ${showCustomize ? styles.show : ''} ${isBottomSheet ? styles.customBottomSheet : ''}`}>
        <div className={styles.overlay} onClick={() => setShowCustomize(false)} />
        <div className={styles.bottomSheetContent}>
          <div className={styles.bottomSheetCloseSection}>
            <div className={styles.customizeBackButton} id="customize-Close" onClick={() => setShowCustomize(false)}><X/></div>
            <div className={styles.bottomSheetHandle} id="customize-handle" onClick={() => setShowCustomize(false)}/>
          </div>

          <div className={styles.bottomSheetHeader}>
            <h3>{t('ItemView.CUSTOMIZE_TITLE')}</h3>
            <button id="customize-reset-button" className={styles.resetButton} onClick={handleReset}>{t('ItemView.RESET')}</button>
          </div>

          <div className={styles.tabContainer}>
            {localModifiers.length > 0 && modifiersFilter(localModifiers, isShowDietaryOptions ? ['dietary', 'notdietary'] : ['notdietary']).map((tab, index) => (
              <button
                id={`customize-tab-${index}`}
                key={ index }
                data-modifiertab={removeSpacesFromString((tab?.modifierName || ''))}
                className={`${styles.tabButton} ${activeTab === index ? styles.activeTab : ''}`}
                onClick={() => {
                  setActiveTab(index);
                  setShowValidationMsg(invalidModifiers.some((m) => m.modifierId === tab.modifierId) );
                  event({
                    action: 'customize_tab_changed',
                    category: 'item_interaction',
                    label: `Tab changed to ${tab?.modifierName ?? ''}`,
                  });
                }}
              >
                <CustomTooltip
                  placement="top"
                  overlayContent={tab?.modifierName ?? ''}
                  scrollElement={document.getElementById('customize-tab-container')}
                >
                  <span>{tab?.modifierName ?? ''}</span>
                </CustomTooltip>
              </button>
            ))}
          </div>
          
          <div className={styles.tabContent}>

          {localModifiers.length > 0 && modifiersFilter(localModifiers, isShowDietaryOptions ? ['dietary', 'notdietary'] : ['notdietary']).map((modifier, index) => (
              <div key={index} id={`customize-mod-${index}`} className={`${styles.optionTabPane} ${activeTab === index ? styles.activeTab : ''}`}>
                {(!ItemNameForAlert || !showValidationMsg) && ( <h4 className={styles.sectionTitle}>{modifier?.modifierName || 'Unknown'}</h4>)}
                {/* <h4 className={showValidationMsg && invalidModifiers.length > 0  ? styles.itemNameWarning : styles.sectionInstruction}>{(showValidationMsg && invalidModifiers.length > 0 )? `Please Add ${invalidModifiers.map((m) => m.modifierName) || 'Customizations'} for  ${ className={styles.itemTitle} ItemNameForAlert ?? ''}`: 'These options are shown for each pizza'}</h4> */}
                <h4
                  className={
                    showValidationMsg && invalidModifiers.length > 0
                      ? styles.itemNameWarning
                      : styles.sectionInstruction
                  }
                >
                  {showValidationMsg && invalidModifiers.length > 0 ? (
                    <>
                      Please add{" "}
                      {invalidModifiers
                        .map(m => `${m.minCount > 1 ? (m.minCount):""} ${m.modifierName} `)
                        .join(", ")}{" "}
                      for{" "}
                      <span className={styles.itemTitleWarning}>
                        {ItemNameForAlert ?? ""}
                      </span>
                    </>
                  ) : (
                    "These options are shown for each pizza"
                  )}
                </h4>

                <div className={styles.optionsGrid}>
                    {modifier?.mofireOptions && [...modifier.mofireOptions]
                    .map((option, originalIndex) => ({ ...option, originalIndex }))
                    .sort((a, b) =>a.isSoldOut === b.isSoldOut ? 0 : a.isSoldOut ? 1 : -1)
                    .map((option: ModifierOption & { originalIndex: number }, optionIndex: number) => (
                    <div key={optionIndex} 
                      id={`customize-mod-${index}-option-${optionIndex}`}
                      onClick={() => {
                        // If single selection and option is sold out, do nothing
                        if(option.isSoldOut && modifier.selectionType === 'SINGLE') return;

                        handleOptionChange(modifier.modifierId, option.originalIndex, false, option.isSoldOut ? false : !option.isApplied, isHalfAndHalf);

                        event({
                          action: 'modifier_option_changed',
                          category: 'item_interaction',
                          label: `Modifier: ${modifier?.modifierName || 'Unknown'}, Option: ${option?.optionName || 'Unknown'}, Applied: ${!option.isApplied}`,
                        });
                      }}
                      className={styles.optionCard + (option.isSoldOut ? ' '+ styles.soldOutCard : '')}
                      style={{ justifyContent: modifier?.allowQuantities ? 'space-between' : 'flex-start', }}
                      data-modifieroption={removeSpacesFromString(option?.optionName || '')}
                      >
                      <div className={styles.optionInfo} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <div className={styles.optionImageWrapper}>
                        <Form.Check 
                        type={modifier?.selectionType === 'MULTIPLE'? 'checkbox' : 'radio'}
                        id={`option-${index}-${optionIndex}`}
                        name={`modifier-${index}`}
                        className={styles.optionCheckbox}
                        checked={option.isApplied || false}
                        disabled={option?.isSoldOut}
                        onChange={() => {}}
                        />
                        <Image 
                        src={option.optionImageUrl?.imageUrl || '/assets/images/ingredient.png'}
                        alt={option.optionName}
                        width={55} 
                        height={55}
                        className={styles.optionImage}
                        placeholder='blur'
                        blurDataURL="/assets/images/placeholder.png"
                        />
                      </div>

                      {option.optionName?.length > 25 ? (
                        <CustomTooltip
                        placement="top"
                        overlayContent={option.optionName}
                        scrollElement={document.getElementById(`customize-mod-${index}`)}
                        >
                        <span className={styles.optionName}>
                          {option.optionName.substring(0, 25) + (option.optionName?.length > 25 ? '...' : '')}
                          {!option?.isSoldOut && option?.optionPrice?.amount ? ` (+${formatCurrency(option.optionPrice.amount,option.optionPrice.currency || 'USD')})` : ''}
                          {option?.isSoldOut && (<span className={styles.soldOutLabel}>{t('ItemView.SOLD_OUT')}</span>)}
                        </span>
                        </CustomTooltip>
                      ) : (
                        <span className={styles.optionName}>
                        {option.optionName.substring(0, 25) + (option.optionName?.length > 25 ? '...' : '')}
                        {!option?.isSoldOut && option?.optionPrice?.amount ? ` (+${formatCurrency((option?.optionPrice?.amount || 0), (option?.optionPrice?.currency || 'USD'))})` : ''}
                        {option?.isSoldOut && <span className={styles.soldOutLabel}>{t('ItemView.SOLD_OUT')}</span>}
                        </span>
                      )}
                      </div>

                      {modifier?.allowQuantities && option.isApplied && (<div className={styles.quantityControl}>
                        <button className={styles.quantityButton} 
                          id={`customize-mod-${index}-option-${optionIndex}-minus`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOptionChange(modifier.modifierId, option.originalIndex, true, -1, isHalfAndHalf);
                          
                            event({
                              action: 'modifier_option_quantity_changed',
                              category: 'item_interaction',
                              label: `Modifier: ${modifier?.modifierName || 'Unknown'}, Option: ${option?.optionName || 'Unknown'}, Decreased by 1`,
                            });
                          }
                          }>
                          <Minus size={14} />
                        </button>
                        <span>{parseInt(option?.quantity.toString() || '1') > 0 ? option?.quantity : 1}</span>

                        <button className={styles.quantityButton} 
                          id={`customize-mod-${index}-option-${optionIndex}-plus`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if(modifier.maxAllowQty && parseInt(option?.quantity.toString() || '1') >= modifier.maxAllowQty){
                              alertService.warn(t('ItemView.MODIFIER_QUANTITY_RESTRICT_NOTICE', { max: modifier.maxAllowQty,}), 3500, true);
                              return;
                            }

                            const increaseVal = Number(option?.quantity) === 0 ? 2 : 1;
                            handleOptionChange(modifier.modifierId, option.originalIndex, true, increaseVal, isHalfAndHalf);

                            event({
                              action: 'modifier_option_quantity_changed',
                              category: 'item_interaction',
                              label: `Modifier: ${modifier?.modifierName || 'Unknown'}, Option: ${option?.optionName || 'Unknown'}, Increased by 1`,
                            });
                          }}>
                          <Plus size={14} />
                        </button>
                    </div>
                    )}
                  </div>
                          )
                        )}
                  </div>
                </div>
              ))}
          </div>
          <div className={styles.bottomSheetFooter}>
            {!ItemNameForAlert && (
            <button className={styles.cancelButton} onClick={() => {
              handleModelClose();
              event({
                action: 'customize_modal_closed',
                category: 'item_interaction',
                label: 'Customize modal closed',
              })
            }}>{t('ItemView.CLOSE')}</button>
            )}
            <button 
              id="customize-update-button"
              disabled={ItemNameForAlert ? invalidModifiers.length > 0 : false}
              onClick={() => {
                if (isFinishStep && ItemNameForAlert ) {
                  onAddToCart?.();
                }
                setShowCustomize(false);
                event({
                  action: 'customize_modal_updated',
                  category: 'item_interaction',
                  label: `Customize modal updated`,
                });
              }}
              className={`${styles.updateButton} ${
                ItemNameForAlert && invalidModifiers.length > 0 ? styles.disabledButton : ''
              }`}
            >
              {ItemNameForAlert
                ? currentStepCount && fullStepCount && currentStepCount < fullStepCount
                  ? `${t('common.updateAndNext')} (${totalPrice})`
                  : `${t('common.updateAndFinish')} (${totalPrice})`
                : `${t('common.update')} (${totalPrice})`}

              {currentStepCount != null &&
                fullStepCount != null &&
                fullStepCount > 1 &&
                currentStepCount !== fullStepCount && currentStepCount < fullStepCount && (
                  <div>
                    <span>
                      ({currentStepCount}/{fullStepCount})
                    </span>
                  </div>
                )}
            </button>
          </div>
        </div>
        {/* <div className={styles.overlay} onClick={() => setShowCustomize(false)}></div> */}
    </div>

    <RemoveCustomizeConfirmation
      show={showRemoveCustomizeConfirmation}
      onClose={() => setShowRemoveCustomizeConfirmation(false)}
      onConfirm={() => {
        setModifiers([...existModifiers as Modifier[]]);
        setLocalModifiers(structuredClone(existModifiers as Modifier[]));
        setShowRemoveCustomizeConfirmation(false);
        setPauseNextStep(false);
      }}
    />
  </>);
}